export * from './parseChartData';

const standardFormatter = new Intl.NumberFormat('et', {
  notation: 'standard',
});

const compactFormatter = new Intl.NumberFormat('et', {
  notation: 'compact',
  compactDisplay: 'short',
});

export const formatNumber = (value: number | bigint) =>
  value < 100_000
    ? standardFormatter.format(value)
    : compactFormatter.format(value);

export const parseNumberValue = (value: string | number): number => {
  if (typeof value === 'number') return value;

  const normalizedValue = value
    .toString()
    .trim()
    .replace(',', '.')
    .replace(' ', '');
  const parsedValue = parseFloat(normalizedValue);

  if (isNaN(parsedValue)) {
    throw new Error(`Invalid number value: ${value}`);
  }

  return parsedValue;
};

export const formatValue = (value: string | number) => {
  try {
    return formatNumber(parseNumberValue(value));
  } catch (error) {
    console.warn(error);
  }
};

/**
 * Converts HTML tags to Unicode characters using regex for subscript and superscript.
 * @example
 * htmlToUnicode('m<sup>2</sup>'); // 'm²'
 * htmlToUnicode('H<sub>2</sub>O'); // 'H₂O'
 * htmlToUnicode('25&deg;C'); // '25°C'
 */
export function htmlToUnicode(value: string) {
  if (!value) return value;

  // Subscript and superscript mappings for digits and certain letters:
  const subMap: Record<string, string> = {
    '0': '₀',
    '1': '₁',
    '2': '₂',
    '3': '₃',
    '4': '₄',
    '5': '₅',
    '6': '₆',
    '7': '₇',
    '8': '₈',
    '9': '₉',
  };

  const supMap: Record<string, string> = {
    '0': '⁰',
    '1': '¹',
    '2': '²',
    '3': '³',
    '4': '⁴',
    '5': '⁵',
    '6': '⁶',
    '7': '⁷',
    '8': '⁸',
    '9': '⁹',
    n: 'ⁿ',
  };

  // Replace <sup>…</sup> content
  let result = value.replace(/<sup>(.+?)<\/sup>/g, (_, content) => {
    return Array.from(content as string[])
      .map((char) => (supMap[char] ? supMap[char] : char))
      .join('');
  });

  // Replace <sub>…</sub> content
  result = result.replace(/<sub>(.+?)<\/sub>/g, (_, content) => {
    return Array.from(content as string[])
      .map((char) => (subMap[char] ? subMap[char] : char))
      .join('');
  });

  // Replace common HTML entities
  const htmlEntities: Record<string, string> = {
    '&deg;': '°',
    '&plusmn;': '±',
    '&times;': '×',
    '&divide;': '÷',
    '&frac12;': '½',
    '&frac14;': '¼',
    '&frac34;': '¾',
  };

  for (const [entity, unicode] of Object.entries(htmlEntities)) {
    result = result.replace(new RegExp(entity, 'g'), unicode);
  }

  return result;
}
